import type { MetricDirectoryEntry } from "../hooks/useMetricDirectory"

/**
 * What a metric reads from, as the catalog records it: the audio itself, the
 * transcript, or the run's OpenTelemetry trace.
 */
export const METRIC_MODALITY = {
  AUDIO: "audio",
  TEXT: "text",
  TRACE: "trace",
} as const

export type MetricModality =
  (typeof METRIC_MODALITY)[keyof typeof METRIC_MODALITY]

export interface MetricModalityInfo {
  modality: MetricModality
  label: string
  description: string
}

const MODALITY_INFO: Record<MetricModality, MetricModalityInfo> = {
  [METRIC_MODALITY.AUDIO]: {
    modality: METRIC_MODALITY.AUDIO,
    label: "Audio",
    description: "Scores the call audio, so it needs a voice conversation.",
  },
  [METRIC_MODALITY.TEXT]: {
    modality: METRIC_MODALITY.TEXT,
    label: "Text",
    description: "Scores the transcript, so it runs on voice and chat alike.",
  },
  [METRIC_MODALITY.TRACE]: {
    modality: METRIC_MODALITY.TRACE,
    label: "Trace",
    description:
      "Scores the run's OpenTelemetry trace, so it needs traces sent to Coval.",
  },
}

/**
 * The catalog's `modality` for a metric, or null when the entry is missing —
 * a custom metric the directory doesn't describe carries no indicator rather
 * than a guessed one.
 */
export function resolveMetricModality(
  entry: MetricDirectoryEntry | undefined,
): MetricModalityInfo | null {
  switch (entry?.modality) {
    case "audio":
      return MODALITY_INFO[METRIC_MODALITY.AUDIO]
    case "transcript":
      return MODALITY_INFO[METRIC_MODALITY.TEXT]
    case "trace":
      return MODALITY_INFO[METRIC_MODALITY.TRACE]
    case undefined:
    default:
      return null
  }
}
